import { supabase } from "@/integrations/supabase/client";
import * as Sentry from "@sentry/react";

export interface VerifyDiscordResult {
  verified: boolean;
  message: string;
  username?: string;
}

// Get the correct URL for Vercel deployments (based on Supabase docs)
const getURL = () => {
  let url =
    import.meta.env.VITE_SITE_URL ?? // Set this in production env
    import.meta.env.VITE_VERCEL_URL ?? // Automatically set by Vercel
    window.location.origin ?? // Fallback to current origin
    'http://localhost:8080/'
  
  // Make sure to include `https://` when not localhost
  url = url.startsWith('http') ? url : `https://${url}`
  
  // Make sure to include a trailing `/`
  url = url.endsWith('/') ? url : `${url}/`
  
  return url
}

// Function to initiate Discord OAuth via Supabase
export async function signInWithDiscord() {
  try {
    // Use Vercel-optimized URL detection
    const baseURL = getURL()
    const redirectTo = `${baseURL}auth/callback`
    
    console.log('=== Discord OAuth Debug ===')
    console.log('Base URL:', baseURL)
    console.log('Redirect URL:', redirectTo)
    console.log('Current origin:', window.location.origin)
    console.log('Environment:', {
      VITE_SITE_URL: import.meta.env.VITE_SITE_URL,
      VITE_VERCEL_URL: import.meta.env.VITE_VERCEL_URL,
      NODE_ENV: import.meta.env.MODE
    })
    
    // Track Discord sign-in attempt
    Sentry.addBreadcrumb({
      message: 'Discord OAuth attempt started',
      category: 'auth',
      level: 'info',
      data: { baseURL, redirectTo }
    });
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'discord',
      options: {
        redirectTo: redirectTo,
        scopes: 'identify email', // Added email scope as recommended
      }
    })

    if (error) {
      console.error('Discord OAuth error:', error)
      Sentry.captureException(error, {
        tags: { component: 'discord_verification', action: 'oauth_signin' },
        extra: { baseURL, redirectTo }
      });
      return { data: null, error }
    }

    console.log('Discord OAuth initiated successfully')
    Sentry.addBreadcrumb({
      message: 'Discord OAuth initiated successfully',
      category: 'auth',
      level: 'info'
    });
    return { data, error: null }
    
  } catch (err) {
    console.error('Discord verification exception:', err)
    Sentry.captureException(err, {
      tags: { component: 'discord_verification', action: 'oauth_signin' },
      extra: { function: 'signInWithDiscord' }
    });
    return { data: null, error: err }
  }
}

// Check if user has a valid Discord session and get username
export async function checkDiscordVerification(): Promise<VerifyDiscordResult> {
  try {
    const { data: { session }, error } = await supabase.auth.getSession()
    
    if (error) {
      console.error('Session error:', error)
      Sentry.captureException(error, {
        tags: { component: 'discord_verification', action: 'check_session' }
      });
      return {
        verified: false,
        message: 'Error checking Discord verification'
      }
    }
    
    console.log('Discord session check:', {
      hasSession: !!session,
      hasUser: !!session?.user,
      provider: session?.user?.app_metadata?.provider,
      userMetadata: session?.user?.user_metadata,
      email: session?.user?.email
    })
    
    // Check if user is authenticated via Discord
    if (!session?.user || session?.user?.app_metadata?.provider !== 'discord') {
      Sentry.addBreadcrumb({
        message: 'Discord verification failed - no valid session',
        category: 'auth',
        level: 'warning',
        data: {
          hasSession: !!session,
          hasUser: !!session?.user,
          provider: session?.user?.app_metadata?.provider
        }
      });
      return {
        verified: false,
        message: 'Please verify with Discord to continue.'
      }
    }
    
    // Extract Discord username from user metadata (more comprehensive)
    const discordUsername = session?.user?.user_metadata?.preferred_username || 
                           session?.user?.user_metadata?.username ||
                           session?.user?.user_metadata?.name || 
                           session?.user?.user_metadata?.full_name ||
                           session?.user?.user_metadata?.custom_claims?.global_name ||
                           session?.user?.email?.split('@')[0] ||
                           'Discord User'
    
    Sentry.addBreadcrumb({
      message: 'Discord verification successful',
      category: 'auth',
      level: 'info',
      data: { username: discordUsername }
    });
    
    return {
      verified: true,
      message: 'Successfully verified with Discord',
      username: discordUsername
    }
  } catch (err) {
    console.error('Discord verification check error:', err)
    Sentry.captureException(err, {
      tags: { component: 'discord_verification', action: 'check_verification' }
    });
    return {
      verified: false,
      message: 'Error checking Discord verification'
    }
  }
}

// Update Discord verification status in the database
export async function updateDiscordVerificationStatus(registrationId: string, verified: boolean, username?: string) {
  try {
    const updateData: any = { discord_verified: verified }
    
    if (username) {
      updateData.discord_username = username
    }
    
    const { data, error } = await supabase
      .from("whitelist_registrations")
      .update(updateData)
      .eq("id", registrationId)

    if (error) {
      console.error('Error updating Discord verification:', error)
      return { data: null, error }
    }

    return { data, error: null }
  } catch (err) {
    console.error('Exception updating Discord verification:', err)
    return { data: null, error: err }
  }
}

// Sign out from Discord/Supabase
export async function signOutDiscord() {
  try {
    const { error } = await supabase.auth.signOut()
    if (error) {
      console.error('Sign out error:', error)
      return { error }
    }
    return { error: null }
  } catch (err) {
    console.error('Sign out exception:', err)
    return { error: err }
  }
}